<template>
  <!-- Modo card: banner e/ou aceite ativos -> tudo envolvido em um container único -->
  <div
    v-if="isWrapped"
    class="instrucoes-card"
    :class="[colorClass, { 'has-error': hasError && !accepted, 'is-accepted': accepted }]"
  >
    <!-- Faixa de orientação no topo -->
    <div
      v-if="cfg.show_read_hint"
      class="instrucoes-hint"
      :class="{ 'is-done': accepted }"
    >
      <span class="instrucoes-hint-icon">
        <svg v-if="!accepted" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3v10M4 9l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M13 4L6 11l-3-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <span class="instrucoes-hint-text">
        {{ accepted
          ? 'Tudo certo! Você já pode prosseguir.'
          : (cfg.read_hint_text || 'Leia todas as instruções até o final para prosseguir.') }}
      </span>
    </div>

    <!-- Conteúdo rico -->
    <div class="instrucoes-content" v-html="contentHtml"></div>

    <!-- Aceite (rodapé do card) -->
    <label
      v-if="cfg.require_acceptance"
      class="instrucoes-accept"
      :class="{ checked: accepted, 'has-error': hasError && !accepted }"
    >
      <span class="instrucoes-checkbox">
        <input type="checkbox" :checked="accepted" @change="onToggle" />
        <span class="instrucoes-checkmark"></span>
      </span>
      <span class="instrucoes-accept-label">
        {{ cfg.acceptance_label || 'Li e estou ciente de todas as instruções acima.' }}
      </span>
    </label>

    <p v-if="hasError && !accepted" class="instrucoes-error">
      Você precisa marcar o aceite das instruções para prosseguir.
    </p>
  </div>

  <!-- Modo simples: apenas o texto, sem card -->
  <div v-else class="instrucoes-content instrucoes-content--plain" :class="colorClass" v-html="contentHtml"></div>
</template>

<script>
import { computed } from 'vue';

const COLORS = ['green', 'amber', 'info', 'danger', 'purple', 'neutral'];

export default {
  name: 'IInstrucoes',
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: [Boolean, String, Number],
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const cfg = computed(() => props.config || {});
    const accepted = computed(() => props.modelValue === true);

    // Vira "card" envolvido quando tem banner e/ou aceite; senão é só texto
    const isWrapped = computed(() => !!cfg.value.show_read_hint || !!cfg.value.require_acceptance);

    // Classe de cor (default green)
    const colorClass = computed(() => {
      const c = cfg.value.color;
      return 'color-' + (COLORS.includes(c) ? c : 'green');
    });

    // Garante que links abram em nova aba
    const contentHtml = computed(() => {
      const html = cfg.value.content || '';
      if (!html) return '';
      return html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
    });

    const onToggle = (e) => emit('update:modelValue', e.target.checked);

    return { cfg, accepted, isWrapped, colorClass, contentHtml, onToggle };
  }
};
</script>

<style scoped>
/* ===== Paleta por cor (CSS vars) ===== */
.color-green {
  --ins-border: #cfe6da;
  --ins-hint-bg: #f0faf5;
  --ins-hint-border: #c2e8d5;
  --ins-hint-text: #1a7a5c;
  --ins-accent: #1a7a5c;
  --ins-accent-bg: #f0faf5;
  --ins-accent-border: #c2e8d5;
}
.color-amber {
  --ins-border: #f0e4bf;
  --ins-hint-bg: #fef8ec;
  --ins-hint-border: #f0ddb0;
  --ins-hint-text: #8a6d2b;
  --ins-accent: #d99a2b;
  --ins-accent-bg: #fdf6e3;
  --ins-accent-border: #f0ddb0;
}
.color-info {
  --ins-border: #d4dcfb;
  --ins-hint-bg: #eef2ff;
  --ins-hint-border: #d4dcfb;
  --ins-hint-text: #3b4fa0;
  --ins-accent: #4f6ef7;
  --ins-accent-bg: #eef2ff;
  --ins-accent-border: #d4dcfb;
}
.color-danger {
  --ins-border: #fbcaca;
  --ins-hint-bg: #fef2f2;
  --ins-hint-border: #fbcaca;
  --ins-hint-text: #b3261e;
  --ins-accent: #dc2626;
  --ins-accent-bg: #fef2f2;
  --ins-accent-border: #fbcaca;
}
.color-purple {
  --ins-border: #ddd6fe;
  --ins-hint-bg: #f5f3ff;
  --ins-hint-border: #ddd6fe;
  --ins-hint-text: #6d28d9;
  --ins-accent: #8b5cf6;
  --ins-accent-bg: #f5f3ff;
  --ins-accent-border: #ddd6fe;
}
.color-neutral {
  --ins-border: #e5e7eb;
  --ins-hint-bg: #f9fafb;
  --ins-hint-border: #e5e7eb;
  --ins-hint-text: #4b5563;
  --ins-accent: #6b7280;
  --ins-accent-bg: #f3f4f6;
  --ins-accent-border: #e5e7eb;
}

/* ===== Card único envolvendo tudo ===== */
.instrucoes-card {
  width: 100%;
  background: #ffffff;
  border: 1px solid var(--ins-border);
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.25s ease;
}
.instrucoes-card.has-error {
  border-color: #f0b4b4;
}

/* Quando aceito, TUDO fica verde — independente da cor escolhida.
   Sobrescreve as CSS vars; banner/aceite/borda/links seguem automaticamente. */
.instrucoes-card.is-accepted {
  --ins-border: #c2e8d5;
  --ins-accent: #1a7a5c;
  --ins-accent-bg: #f0faf5;
  --ins-accent-border: #c2e8d5;
}

/* ===== Faixa de orientação ===== */
.instrucoes-hint {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 18px;
  background: var(--ins-hint-bg);
  border-bottom: 1px solid var(--ins-hint-border);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--ins-hint-text);
  transition: background 0.35s ease, color 0.35s ease, border-color 0.35s ease;
}
.instrucoes-hint.is-done {
  background: var(--ins-accent-bg);
  border-bottom-color: var(--ins-accent-border);
  color: var(--ins-accent);
}
.instrucoes-hint-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.instrucoes-hint:not(.is-done) .instrucoes-hint-icon {
  animation: instrucoes-bounce 1.6s ease-in-out infinite;
}
.instrucoes-hint-text { flex: 1; }

@keyframes instrucoes-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

/* ===== Conteúdo ===== */
.instrucoes-content {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: #4a453f;
}
.instrucoes-card .instrucoes-content {
  padding: 18px 20px;
}
.instrucoes-content--plain {
  width: 100%;
}

.instrucoes-content :deep(p) { margin: 0 0 0.75rem; }
.instrucoes-content :deep(p:last-child) { margin-bottom: 0; }

/* Negrito/itálico — o reset global (* { font-weight: 300 }) mata o <strong>,
   então forçamos o peso aqui pra o destaque do editor funcionar */
.instrucoes-content :deep(strong),
.instrucoes-content :deep(b) { font-weight: 700; }
.instrucoes-content :deep(em),
.instrucoes-content :deep(i) { font-style: italic; }
.instrucoes-content :deep(u) { text-decoration: underline; }
.instrucoes-content :deep(s) { text-decoration: line-through; }

.instrucoes-content :deep(h2) {
  font-size: 1.35em;
  font-weight: 700;
  color: #2c2926;
  margin: 0.8rem 0 0.5rem;
  line-height: 1.3;
}
.instrucoes-content :deep(h3) {
  font-size: 1.15em;
  font-weight: 600;
  color: #2c2926;
  margin: 0.7rem 0 0.4rem;
  line-height: 1.35;
}

.instrucoes-content :deep(ul) {
  list-style: disc outside;
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}
.instrucoes-content :deep(ol) {
  list-style: decimal outside;
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}
.instrucoes-content :deep(li) { margin-bottom: 0.25rem; }

.instrucoes-content :deep(.text-tiny) { font-size: 0.7em; }
.instrucoes-content :deep(.text-small) { font-size: 0.85em; }
.instrucoes-content :deep(.text-big) { font-size: 1.4em; }
.instrucoes-content :deep(.text-huge) { font-size: 1.8em; }

.instrucoes-content :deep(blockquote) {
  border-left: 3px solid #d1d5db;
  padding-left: 1rem;
  margin: 0.75rem 0;
  color: #6b7280;
  font-style: italic;
}
.instrucoes-content :deep(hr) {
  border: none;
  border-top: 1px solid #e8e4de;
  margin: 1rem 0;
}
.instrucoes-content :deep(a),
.instrucoes-content :deep(a:visited),
.instrucoes-content :deep(a:active),
.instrucoes-content :deep(a:focus) {
  color: var(--ins-accent, #1a7a5c) !important;
  text-decoration: underline;
}
.instrucoes-content :deep(a:hover) { opacity: 0.8; }

/* ===== Aceite (rodapé do card) ===== */
.instrucoes-accept {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid var(--ins-border);
  background: #fdfcfb;
  cursor: pointer;
  user-select: none;
  transition: background 0.25s ease, border-color 0.25s ease;
}
.instrucoes-accept:hover { background: #f7f5f2; }
.instrucoes-accept.checked {
  background: var(--ins-accent-bg);
  border-top-color: var(--ins-accent-border);
}
.instrucoes-accept.has-error {
  background: #fef4f4;
  border-top-color: #f0b4b4;
}

.instrucoes-checkbox {
  position: relative;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-top: 1px;
}
.instrucoes-checkbox input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  z-index: 1;
}
.instrucoes-checkmark {
  position: absolute;
  inset: 0;
  background: #ffffff;
  border: 2px solid #ccc8c1;
  border-radius: 6px;
  transition: all 0.25s ease;
}
.instrucoes-checkbox input:checked ~ .instrucoes-checkmark {
  background: var(--ins-accent);
  border-color: var(--ins-accent);
}
.instrucoes-accept.has-error .instrucoes-checkmark {
  border-color: #e08080;
}
.instrucoes-checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 11px;
  border: solid #fff;
  border-width: 0 2.5px 2.5px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.instrucoes-checkbox input:checked ~ .instrucoes-checkmark::after {
  transform: rotate(45deg) scale(1);
}

.instrucoes-accept-label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.55;
  color: #3d3833;
}

.instrucoes-error {
  margin: 0;
  padding: 8px 20px 12px;
  font-size: 0.75rem;
  color: #dc2626;
}

@media (max-width: 480px) {
  .instrucoes-card .instrucoes-content { padding: 16px; }
  .instrucoes-accept { padding: 14px 16px; }
  .instrucoes-hint { padding: 11px 16px; }
}
</style>
